"use client";

// Daily portfolio summary — a flat, row-per-account view (not a cycle pivot):
//   1. Account   — clickable (row → /accounts/[id]) + client-code pill
//   2. Daily P&L — each account's latest EOD net P&L + freshness pill
//   3. Current cycle — that account's P&L in the current NIFTY cycle, the
//      column header carrying the clear month label (e.g. 2026JUL)
//
// Built entirely from two existing endpoints — no new backend:
//   api.portfolioSummary()     → account meta + per-cycle P&L (current cycle)
//   api.portfolioDailyLatest()  → per-account latest EOD day-net + date/today

import { useEffect, useMemo, useState } from "react";
import {
  AccountBadge,
  Crumbs,
  Page,
  Td,
  ToggleChip,
  Tr,
} from "@/components/ui";
import { DataTable, type ColumnConfig } from "@/components/DataTable";
import { EodPill } from "@/components/EodPill";
import { HttpError, PortfolioDailyLatestRow, PortfolioSummaryRow, api } from "@/lib/api";
import { fmtCycleLabel, fmtRupeesShort, pnlClass } from "@/lib/fmt";

type DailyRow = {
  account: string;
  account_id: number;
  is_client_account: boolean;
  clientcode: string | null;
  daily_pnl: number | null;
  daily_date: string | null;
  daily_is_today: boolean;
  cycle_pnl: number | null;
};

export default function PortfolioDailyPage() {
  const [summary, setSummary] = useState<PortfolioSummaryRow[] | null>(null);
  const [daily, setDaily] = useState<PortfolioDailyLatestRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    setErr(null);
    setSummary(null);
    setDaily(null);
    api.portfolioSummary(activeOnly)
      .then(setSummary)
      .catch((e) =>
        setErr(e instanceof HttpError && e.status === 403 ? "Admin-only report." : String(e)),
      );
    // Non-fatal — the table still renders (Daily column shows "—") if this fails.
    api.portfolioDailyLatest(activeOnly).then(setDaily).catch(() => setDaily([]));
  }, [activeOnly]);

  // One row per account: current-cycle P&L (latest cycle by cycle_start) +
  // the joined latest-EOD daily P&L. Account order best-first by cycle P&L.
  const { rows, currentCycle } = useMemo(() => {
    if (!summary) return { rows: null as DailyRow[] | null, currentCycle: "" };

    let currentCycle = "";
    let bestStart = "";
    for (const r of summary) {
      if (r.cycle_start > bestStart) {
        bestStart = r.cycle_start;
        currentCycle = r.cycle_label;
      }
    }

    const dailyMap = new Map<number, PortfolioDailyLatestRow>();
    for (const d of daily ?? []) dailyMap.set(d.account_id, d);

    const byId = new Map<number, DailyRow>();
    for (const r of summary) {
      if (!byId.has(r.account_id)) {
        const d = dailyMap.get(r.account_id);
        byId.set(r.account_id, {
          account: r.account,
          account_id: r.account_id,
          is_client_account: r.is_client_account,
          clientcode: r.clientcode,
          daily_pnl: d ? Number(d.daily_pnl_net_rs) : null,
          daily_date: d ? d.as_of_date : null,
          daily_is_today: d ? d.is_today : false,
          cycle_pnl: null,
        });
      }
      if (r.cycle_label === currentCycle) {
        byId.get(r.account_id)!.cycle_pnl = Number(r.pnl_net_rs);
      }
    }

    const rows = [...byId.values()].sort(
      (a, b) => (b.cycle_pnl ?? 0) - (a.cycle_pnl ?? 0),
    );
    return { rows, currentCycle };
  }, [summary, daily]);

  if (err) return <p className="text-sm text-down">{err}</p>;
  if (!summary || !rows) return <p className="eyebrow text-mute">Loading…</p>;

  const cycleLabel = fmtCycleLabel(currentCycle); // e.g. "2026JUL"

  const columns: ColumnConfig<DailyRow>[] = [
    {
      key: "account",
      header: "Account",
      cell: (r) => (
        <span className="flex items-center gap-2">
          <span className="text-foreground">{r.account}</span>
          <AccountBadge is_client_account={r.is_client_account} clientcode={r.clientcode} accountId={r.account_id} />
        </span>
      ),
      sortAccessor: (r) => r.account,
      exportValue: (r) => r.account,
    },
    {
      key: "daily",
      header: "Daily P&L",
      align: "right",
      cell: (r) =>
        r.daily_pnl == null ? (
          <span className="text-mute">—</span>
        ) : (
          <span className="inline-flex items-center justify-end gap-2">
            {r.daily_date && <EodPill date={r.daily_date} isToday={r.daily_is_today} />}
            <span className={pnlClass(r.daily_pnl)}>{fmtRupeesShort(r.daily_pnl)}</span>
          </span>
        ),
      sortAccessor: (r) => r.daily_pnl ?? 0,
      exportValue: (r) => (r.daily_pnl == null ? "" : r.daily_pnl),
    },
    {
      key: "cycle",
      header: cycleLabel || "Current cycle",
      headerHelp: `P&L in the current NIFTY expiry cycle (${cycleLabel}).`,
      align: "right",
      cell: (r) =>
        r.cycle_pnl == null ? (
          <span className="text-mute">—</span>
        ) : (
          <span className={pnlClass(r.cycle_pnl)}>{fmtRupeesShort(r.cycle_pnl)}</span>
        ),
      sortAccessor: (r) => r.cycle_pnl ?? 0,
      exportValue: (r) => (r.cycle_pnl == null ? "" : r.cycle_pnl),
    },
  ];

  const totalDaily = rows.reduce((s, r) => s + (r.daily_pnl ?? 0), 0);
  const totalCycle = rows.reduce((s, r) => s + (r.cycle_pnl ?? 0), 0);
  const totalsRow = (
    <Tr>
      <Td sticky className="font-semibold text-foreground">Σ total</Td>
      <Td align="right" className={`font-semibold ${pnlClass(totalDaily)}`}>
        {fmtRupeesShort(totalDaily)}
      </Td>
      <Td align="right" className={`font-semibold ${pnlClass(totalCycle)}`}>
        {fmtRupeesShort(totalCycle)}
      </Td>
    </Tr>
  );

  return (
    <Page
      eyebrow={<Crumbs items={[{ label: "Reports" }, { label: "Portfolio summary (Daily)" }]} />}
      title={<>Daily <em className="italic">portfolio</em> summary</>}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ToggleChip selected={activeOnly} onClick={() => setActiveOnly((v) => !v)}>
          Active only
        </ToggleChip>
        <div className="ml-auto eyebrow text-mute">
          {rows.length} accounts · current cycle {cycleLabel}
        </div>
      </div>

      <DataTable
        tableKey="portfolio-daily"
        stickyFirstColumn
        data={rows}
        columns={columns}
        rowKey={(r) => String(r.account_id)}
        asLinkHref={(r) => `/accounts/${r.account_id}`}
        totalsRow={totalsRow}
        defaultSort={{ key: "cycle", dir: "desc" }}
        paginate={false}
      />
    </Page>
  );
}
